import React, { useState, useEffect, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import api from '../../../services/api';

/**
 * Komunikator - podglad wlasciciela.
 *
 * Zakladka TYLKO DO ODCZYTU: pokazuje cala historie rozmow z komunikatora
 * (kanal "Cala siec", kanaly sklepowe i rozmowy prywatne 1:1), razem z
 * wiadomosciami usunietymi przez autorow - tresc nigdy nie znika z bazy,
 * wiec przy sporze widac oryginal.
 *
 * Endpointy (wszystkie @require_admin, blueprint komunikator_admin):
 *   GET /komunikator/admin/rozmowy?typ=&login=&od=&do=&limit=&offset=
 *   GET /komunikator/admin/rozmowy/<id>/wiadomosci?limit=&offset=   (rosnaco po id)
 *   GET /komunikator/admin/szukaj?q=&typ=&login=&od=&do=
 *   GET /komunikator/zalacznik/<id>[?thumb=1]                       (@require_auth)
 * Pomocniczo (istniejacy endpoint, admin omija sprawdzanie uprawnien):
 *   GET /messages/users - lista loginow do filtra "pracownik"
 *
 * UWAGA - zalaczniki pobieram BLOBEM przez axios, nie przez <img src="/api/...">.
 * Bezposredni URL leci bez naglowka X-Device-Token, a require_auth przy braku
 * tokena robi session.clear() + 403 (auth.py:106-116) - czyli obrazek moglby
 * WYLOGOWAC admina. Instancja api dokleja token sama (services/api.js:93-96).
 *
 * UI standard V3SYS: przyciski 12px / 6px 12px, male 11px / 4px 10px,
 * aktywny toggle = pelne tlo + #fff, kolory wylacznie z tokenow var(--...).
 * Kazdy przycisk ma tlo podane INLINE - inaczej globalna regula motywu
 * (v3sys-theme.css:962-968, wyjatek :not([style*="background"])) nadpisuje je
 * przez !important i zaznaczenie przestaje byc widoczne.
 */

// ---------------------------------------------------------------------------
// Stale
// ---------------------------------------------------------------------------

const STRONA_ROZMOW = 200;        // ile rozmow na raz (backend max 500)
const STRONA_WIADOMOSCI = 200;    // ile wiadomosci na raz (backend max 500)
const STRONA_EKSPORTU = 500;      // eksport - maksymalna strona backendu
const MAX_WIADOMOSCI_EKSPORT = 50000;
const STATY_MAX_ROZMOW = 15;      // ile rozmow doczytac przy liczeniu statystyk
const STATY_OKNO = 300;           // ile ostatnich wiadomosci obejrzec w rozmowie

const TYPY = [
  { id: '', label: 'Wszystkie', ikona: '📋' },
  { id: 'globalny', label: 'Cała sieć', ikona: '🌐' },
  { id: 'sklep', label: 'Sklep', ikona: '🏬' },
  { id: 'prywatna', label: 'Prywatne', ikona: '🔒' }
];

const KOLOR_TYPU = {
  globalny: 'var(--info)',
  sklep: 'var(--purple)',
  prywatna: 'var(--orange)'
};

const IKONA_TYPU = {
  globalny: '🌐',
  sklep: '🏬',
  prywatna: '🔒'
};

// ---------------------------------------------------------------------------
// Pomocniki HTTP
// ---------------------------------------------------------------------------

// To jest axios, NIE fetch - odpowiedz nie ma pola `.ok`, ma `status`.
const czyOk = (res) => res && typeof res.status === 'number' && res.status >= 200 && res.status < 300;

// Backend owija odpowiedz: { success, data, message, status_code }
const trescOdpowiedzi = (res) => (czyOk(res) ? (res.data && res.data.data) || null : null);

const tekstBledu = (e, domyslny) => {
  if (e && e.status === 403) return 'Brak uprawnień administratora do podglądu komunikatora';
  return (e && (e.data?.error || e.data?.message)) || e?.message || domyslny;
};

/**
 * Kolejka pobran o ograniczonej rownoleglosci.
 * Limit zapytan to 200/min PER IP (rate_limiter.py), a caly sklep siedzi za
 * jednym NAT-em - rozmowa z kilkudziesiecioma zdjeciami nie moze wystrzelic
 * kilkudziesieciu zapytan naraz.
 */
const kolejka = [];
let biegnace = 0;
const MAX_ROWNOLEGLE = 3;

// Uruchamianie wyniesione poza petle - domkniecie tworzone w ciele `while`
// lamie regule no-loop-func, a build produkcyjny (CI=true) traktuje
// ostrzezenia lintera jak bledy.
function uruchomZKolejki(pozycja) {
  biegnace += 1;
  Promise.resolve()
    .then(pozycja.zadanie)
    .then(pozycja.resolve, pozycja.reject)
    .finally(() => { biegnace -= 1; pchnijKolejke(); });
}

function pchnijKolejke() {
  while (biegnace < MAX_ROWNOLEGLE && kolejka.length > 0) {
    uruchomZKolejki(kolejka.shift());
  }
}

const wKolejce = (zadanie) => new Promise((resolve, reject) => {
  kolejka.push({ zadanie, resolve, reject });
  pchnijKolejke();
});

// ---------------------------------------------------------------------------
// Pomocniki dat i formatowania
// ---------------------------------------------------------------------------

// Backend oddaje 'RRRR-MM-DD GG:MM:SS' (isoformat ze spacja). Safari nie parsuje
// tego formatu natywnie, wiec rozbijam recznie.
const parsujDate = (wartosc) => {
  if (!wartosc) return null;
  const m = String(wartosc).match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?/);
  if (m) {
    return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]),
      Number(m[4]), Number(m[5]), Number(m[6] || 0));
  }
  const d = new Date(wartosc);
  return isNaN(d.getTime()) ? null : d;
};

const dzienZTekstu = (wartosc) => (wartosc ? String(wartosc).slice(0, 10) : '');

const dzisiajKlucz = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const fmtDataGodzina = (wartosc) => {
  const d = parsujDate(wartosc);
  return d ? d.toLocaleString('pl-PL') : '-';
};

const fmtGodzina = (wartosc) => {
  const d = parsujDate(wartosc);
  return d ? d.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' }) : '';
};

const fmtDzienOpisowo = (klucz) => {
  const d = parsujDate(`${klucz} 00:00:00`);
  if (!d) return klucz;
  const dzis = dzisiajKlucz();
  if (klucz === dzis) return 'Dzisiaj';
  const wczoraj = new Date();
  wczoraj.setDate(wczoraj.getDate() - 1);
  const p = (n) => String(n).padStart(2, '0');
  const kluczWczoraj = `${wczoraj.getFullYear()}-${p(wczoraj.getMonth() + 1)}-${p(wczoraj.getDate())}`;
  if (klucz === kluczWczoraj) return 'Wczoraj';
  return d.toLocaleDateString('pl-PL', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' });
};

const fmtRozmiar = (bajty) => {
  const b = Number(bajty || 0);
  if (!b) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} kB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

const skrot = (tekst, ile) => {
  const t = String(tekst || '');
  return t.length > ile ? `${t.slice(0, ile)}…` : t;
};

// ---------------------------------------------------------------------------
// Style wspolne
// ---------------------------------------------------------------------------

const kartaStyl = {
  backgroundColor: 'var(--card-bg)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius)',
  overflow: 'hidden'
};

const naglowekKartyStyl = {
  padding: '0.6rem 0.75rem',
  borderBottom: '1px solid var(--border)',
  backgroundColor: 'var(--bg)',
  fontWeight: 600,
  fontSize: '12px',
  color: 'var(--text-secondary)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '0.5rem',
  flexWrap: 'wrap'
};

// Tlo ZAWSZE inline - patrz komentarz na gorze pliku.
const przycisk = (aktywny, kolor) => ({
  padding: '6px 12px',
  fontSize: '12px',
  fontWeight: aktywny ? 600 : 500,
  cursor: 'pointer',
  borderRadius: '6px',
  backgroundColor: aktywny ? (kolor || 'var(--primary)') : 'var(--card-bg)',
  border: `1px solid ${aktywny ? (kolor || 'var(--primary)') : 'var(--border)'}`,
  color: aktywny ? '#fff' : 'var(--text-secondary)',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.35rem',
  whiteSpace: 'nowrap'
});

const malyPrzycisk = (aktywny, kolor) => ({
  ...przycisk(aktywny, kolor),
  padding: '4px 10px',
  fontSize: '11px'
});

const polePolStyl = {
  width: '100%',
  padding: '6px 10px',
  fontSize: '12px',
  borderRadius: '6px',
  border: '1px solid var(--border)',
  backgroundColor: 'var(--card-bg)',
  color: 'var(--text)'
};

const etykietaStyl = {
  display: 'block',
  fontSize: '11px',
  fontWeight: 600,
  color: 'var(--text-muted)',
  marginBottom: '0.2rem'
};

const plakietka = (kolor) => ({
  display: 'inline-block',
  padding: '1px 6px',
  fontSize: '10px',
  fontWeight: 600,
  borderRadius: '4px',
  border: `1px solid ${kolor}`,
  color: kolor,
  backgroundColor: 'transparent',
  whiteSpace: 'nowrap'
});

// ---------------------------------------------------------------------------
// Zalacznik (miniatura / kafelek pliku)
// ---------------------------------------------------------------------------

const Zalacznik = ({ zalacznik }) => {
  const [urlPodgladu, setUrlPodgladu] = useState(null);
  const [blad, setBlad] = useState(false);
  const [pobieranie, setPobieranie] = useState(false);

  useEffect(() => {
    if (!zalacznik.czy_obraz) return undefined;
    let anulowane = false;
    let utworzonyUrl = null;

    wKolejce(() => api.get(`/komunikator/zalacznik/${zalacznik.id}`, {
      params: zalacznik.ma_miniature ? { thumb: 1 } : {},
      responseType: 'blob'
    }))
      .then((res) => {
        if (anulowane || !czyOk(res)) return;
        utworzonyUrl = URL.createObjectURL(res.data);
        setUrlPodgladu(utworzonyUrl);
      })
      .catch(() => { if (!anulowane) setBlad(true); });

    return () => {
      anulowane = true;
      if (utworzonyUrl) URL.revokeObjectURL(utworzonyUrl);
    };
  }, [zalacznik.id, zalacznik.czy_obraz, zalacznik.ma_miniature]);

  const pobierzOryginal = async () => {
    setPobieranie(true);
    try {
      const res = await wKolejce(() => api.get(`/komunikator/zalacznik/${zalacznik.id}`, { responseType: 'blob' }));
      if (!czyOk(res)) throw new Error('Nie udało się pobrać pliku');
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = zalacznik.plik_nazwa || `zalacznik_${zalacznik.id}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(tekstBledu(e, 'Nie udało się pobrać załącznika'));
    } finally {
      setPobieranie(false);
    }
  };

  if (zalacznik.czy_obraz) {
    return (
      <button
        type="button"
        onClick={pobierzOryginal}
        title={`${zalacznik.plik_nazwa || 'obraz'} ${fmtRozmiar(zalacznik.rozmiar_bajty)} - kliknij, aby pobrać oryginał`}
        style={{
          padding: '0',
          backgroundColor: 'var(--bg)',
          border: '1px solid var(--border)',
          borderRadius: '6px',
          overflow: 'hidden',
          cursor: 'pointer',
          width: '132px',
          height: '132px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0
        }}
      >
        {urlPodgladu ? (
          <img
            src={urlPodgladu}
            alt={zalacznik.plik_nazwa || 'załącznik'}
            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'cover', display: 'block' }}
          />
        ) : (
          <span style={{ fontSize: '11px', color: blad ? 'var(--danger)' : 'var(--text-muted)', padding: '0.5rem', textAlign: 'center' }}>
            {blad ? 'Nie wczytano' : 'Wczytywanie…'}
          </span>
        )}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={pobierzOryginal}
      disabled={pobieranie}
      title={zalacznik.plik_nazwa || 'plik'}
      style={{
        padding: '6px 10px',
        fontSize: '11px',
        fontWeight: 500,
        cursor: 'pointer',
        borderRadius: '6px',
        backgroundColor: 'var(--bg)',
        border: '1px solid var(--border)',
        color: 'var(--text-secondary)',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.4rem',
        maxWidth: '100%',
        minWidth: 0
      }}
    >
      <span aria-hidden="true">📎</span>
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '220px' }}>
        {zalacznik.plik_nazwa || `plik ${zalacznik.id}`}
      </span>
      <span style={{ color: 'var(--text-muted)' }}>{fmtRozmiar(zalacznik.rozmiar_bajty)}</span>
      {pobieranie && <span style={{ color: 'var(--text-muted)' }}>…</span>}
    </button>
  );
};

// ---------------------------------------------------------------------------
// Kafelek statystyki
// ---------------------------------------------------------------------------

const Kafelek = ({ ikona, etykieta, wartosc, kolor, przypis }) => (
  <div style={{
    ...kartaStyl,
    padding: '0.6rem 0.75rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.6rem',
    minWidth: 0
  }}>
    <span style={{ fontSize: '18px' }} aria-hidden="true">{ikona}</span>
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: '18px', fontWeight: 700, color: kolor || 'var(--text)', lineHeight: 1.1 }}>
        {wartosc}
      </div>
      <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{etykieta}</div>
      {przypis && <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>{przypis}</div>}
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Glowny komponent
// ---------------------------------------------------------------------------

const KomunikatorTab = () => {
  // --- filtry listy rozmow ---
  const [filtry, setFiltry] = useState({ typ: '', login: '', od: '', do: '' });
  const [uzyteFiltry, setUzyteFiltry] = useState({ typ: '', login: '', od: '', do: '' });

  // --- lista rozmow ---
  const [rozmowy, setRozmowy] = useState([]);
  const [rozmowyTotal, setRozmowyTotal] = useState(0);
  const [rozmowyLoading, setRozmowyLoading] = useState(false);
  const [rozmowyBlad, setRozmowyBlad] = useState(null);

  // --- wybrana rozmowa ---
  const [rozmowa, setRozmowa] = useState(null);
  const [wiadomosci, setWiadomosci] = useState([]);
  const [wiadomosciTotal, setWiadomosciTotal] = useState(0);
  const [zakresOd, setZakresOd] = useState(0);   // offset pierwszej wczytanej
  const [zakresDo, setZakresDo] = useState(0);   // offset za ostatnia wczytana
  const [historiaLoading, setHistoriaLoading] = useState(false);
  const [doczytywanie, setDoczytywanie] = useState(false);
  const [historiaBlad, setHistoriaBlad] = useState(null);
  const [podswietlona, setPodswietlona] = useState(null);

  // --- wyszukiwarka ---
  const [fraza, setFraza] = useState('');
  const [wyniki, setWyniki] = useState(null);   // null = tryb listy rozmow
  const [wynikiObciete, setWynikiObciete] = useState(false);
  const [szukanieLoading, setSzukanieLoading] = useState(false);

  // --- statystyki ---
  const [staty, setStaty] = useState(null);
  const [statyLoading, setStatyLoading] = useState(false);

  // --- slownik pracownikow do filtra ---
  const [pracownicy, setPracownicy] = useState([]);

  // --- eksport ---
  const [eksportTrwa, setEksportTrwa] = useState(false);

  const przewijaneRef = useRef(null);
  const wiadomosciRef = useRef({});
  const przewinDoDolu = useRef(false);

  // -------------------------------------------------------------------------
  // Pobieranie danych
  // -------------------------------------------------------------------------

  const wczytajRozmowy = useCallback(async (f, dopisz) => {
    setRozmowyLoading(true);
    setRozmowyBlad(null);
    try {
      const params = { limit: STRONA_ROZMOW, offset: dopisz ? rozmowy.length : 0 };
      if (f.typ) params.typ = f.typ;
      if (f.login) params.login = f.login;
      if (f.od) params.od = f.od;
      if (f.do) params.do = f.do;

      const res = await api.get('/komunikator/admin/rozmowy', { params });
      const d = trescOdpowiedzi(res);
      if (!d) throw new Error('Nieprawidłowa odpowiedź serwera');
      const lista = Array.isArray(d.rozmowy) ? d.rozmowy : [];
      setRozmowy((poprzednie) => (dopisz ? [...poprzednie, ...lista] : lista));
      setRozmowyTotal(Number(d.total) || 0);
    } catch (e) {
      setRozmowyBlad(tekstBledu(e, 'Nie udało się pobrać listy rozmów'));
      if (!dopisz) { setRozmowy([]); setRozmowyTotal(0); }
    } finally {
      setRozmowyLoading(false);
    }
  }, [rozmowy.length]);

  const wczytajPracownikow = useCallback(async () => {
    try {
      const res = await api.get('/messages/users', { params: { location_id: 'all', all_locations: '1' } });
      const d = trescOdpowiedzi(res);
      const lista = Array.isArray(d) ? d : [];
      // endpoint zwraca login pod kluczem `nazwa`
      setPracownicy(lista.map((u) => u.nazwa).filter(Boolean));
    } catch (e) {
      setPracownicy([]);   // filtr zadziala jako zwykle pole tekstowe
    }
  }, []);

  /**
   * Statystyki liczone WYLACZNIE z istniejacych endpointow:
   * lista rozmow z filtrem od=do=dzis daje rozmowy aktywne dzisiaj, a z kazdej
   * z nich doczytuje ogon historii i licze wiadomosci z dzisiejsza data.
   * Przy bardzo ruchliwym dniu okno moze nie objac wszystkiego - wtedy wynik
   * jest oznaczony jako "co najmniej" (≥).
   */
  const przeliczStatystyki = useCallback(async () => {
    setStatyLoading(true);
    try {
      const dzis = dzisiajKlucz();

      const [resDzis, resWszystkie] = await Promise.all([
        api.get('/komunikator/admin/rozmowy', { params: { od: dzis, do: dzis, limit: STRONA_ROZMOW } }),
        api.get('/komunikator/admin/rozmowy', { params: { limit: 1 } })
      ]);

      const dDzis = trescOdpowiedzi(resDzis);
      const dWszystkie = trescOdpowiedzi(resWszystkie);
      const aktywneDzis = dDzis && Array.isArray(dDzis.rozmowy) ? dDzis.rozmowy : [];

      const doPobrania = aktywneDzis.slice(0, STATY_MAX_ROZMOW);
      let niepelne = aktywneDzis.length > doPobrania.length;

      const strony = await Promise.all(doPobrania.map((r) => wKolejce(() => api.get(
        `/komunikator/admin/rozmowy/${r.id}/wiadomosci`,
        { params: { limit: STATY_OKNO, offset: Math.max(0, (Number(r.liczba_wiadomosci) || 0) - STATY_OKNO) } }
      ))
        .then((res) => ({ dane: trescOdpowiedzi(res), rozmowa: r }))
        .catch(() => { niepelne = true; return null; })));

      let wiadomosciDzis = 0;
      const autorzy = new Set();

      strony.forEach((strona) => {
        if (!strona || !strona.dane) return;
        const lista = Array.isArray(strona.dane.wiadomosci) ? strona.dane.wiadomosci : [];
        lista.forEach((w) => {
          if (dzienZTekstu(w.utworzono) === dzis) {
            wiadomosciDzis += 1;
            if (w.autor_login) autorzy.add(w.autor_login);
          }
        });
        // Jesli najstarsza wczytana wiadomosc jest z dzisiaj, a przed nia byly
        // jeszcze inne - okno ich nie objelo.
        const offset = Number(strona.dane.offset) || 0;
        if (offset > 0 && lista.length > 0 && dzienZTekstu(lista[0].utworzono) === dzis) {
          niepelne = true;
        }
      });

      setStaty({
        wiadomosciDzis,
        rozmowyDzis: Number(dDzis && dDzis.total) || aktywneDzis.length,
        piszacyDzis: autorzy.size,
        rozmowyWSystemie: Number(dWszystkie && dWszystkie.total) || 0,
        niepelne
      });
    } catch (e) {
      setStaty(null);
      toast.error(tekstBledu(e, 'Nie udało się policzyć statystyk'));
    } finally {
      setStatyLoading(false);
    }
  }, []);

  useEffect(() => {
    wczytajRozmowy({ typ: '', login: '', od: '', do: '' }, false);
    wczytajPracownikow();
    przeliczStatystyki();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // -------------------------------------------------------------------------
  // Historia rozmowy
  // -------------------------------------------------------------------------

  /** Binarne wyszukanie pozycji wiadomosci w rozmowie (id rosna razem z offsetem). */
  const znajdzPozycje = useCallback(async (rozmowaId, celId, total) => {
    if (!celId || total <= STRONA_WIADOMOSCI) return 0;
    let lo = 0;
    let hi = total - 1;
    let znaleziony = 0;
    let kroki = 0;
    while (lo <= hi && kroki < 24) {
      kroki += 1;
      const mid = Math.floor((lo + hi) / 2);
      // eslint-disable-next-line no-await-in-loop
      const res = await api.get(`/komunikator/admin/rozmowy/${rozmowaId}/wiadomosci`, { params: { limit: 1, offset: mid } });
      const d = trescOdpowiedzi(res);
      const w = d && Array.isArray(d.wiadomosci) ? d.wiadomosci[0] : null;
      if (!w) { hi = mid - 1; continue; }
      if (w.id === celId) { znaleziony = mid; break; }
      if (w.id < celId) { znaleziony = mid; lo = mid + 1; } else { hi = mid - 1; }
    }
    return Math.max(0, znaleziony - Math.floor(STRONA_WIADOMOSCI / 2));
  }, []);

  const otworzRozmowe = useCallback(async (rozmowaId, celId) => {
    setHistoriaLoading(true);
    setHistoriaBlad(null);
    setPodswietlona(celId || null);
    wiadomosciRef.current = {};
    try {
      // Pierwsze zapytanie ustala total i naglowek rozmowy.
      const wstepne = await api.get(`/komunikator/admin/rozmowy/${rozmowaId}/wiadomosci`, { params: { limit: 1, offset: 0 } });
      const dWstepne = trescOdpowiedzi(wstepne);
      if (!dWstepne) throw new Error('Nieprawidłowa odpowiedź serwera');
      const total = Number(dWstepne.total) || 0;
      setRozmowa(dWstepne.rozmowa || null);
      setWiadomosciTotal(total);

      if (total === 0) {
        setWiadomosci([]);
        setZakresOd(0);
        setZakresDo(0);
        return;
      }

      let offset;
      if (celId) {
        offset = await znajdzPozycje(rozmowaId, celId, total);
      } else {
        offset = Math.max(0, total - STRONA_WIADOMOSCI);
      }
      offset = Math.min(offset, Math.max(0, total - 1));

      const res = await api.get(`/komunikator/admin/rozmowy/${rozmowaId}/wiadomosci`, { params: { limit: STRONA_WIADOMOSCI, offset } });
      const d = trescOdpowiedzi(res);
      const lista = d && Array.isArray(d.wiadomosci) ? d.wiadomosci : [];
      setRozmowa(d && d.rozmowa ? d.rozmowa : dWstepne.rozmowa || null);
      setWiadomosci(lista);
      setZakresOd(offset);
      setZakresDo(offset + lista.length);
      setWiadomosciTotal(Number(d && d.total) || total);
      przewinDoDolu.current = !celId;
    } catch (e) {
      setHistoriaBlad(tekstBledu(e, 'Nie udało się pobrać historii rozmowy'));
      setWiadomosci([]);
      setWiadomosciTotal(0);
    } finally {
      setHistoriaLoading(false);
    }
  }, [znajdzPozycje]);

  const doczytajStarsze = useCallback(async () => {
    if (!rozmowa || zakresOd <= 0 || doczytywanie) return;
    setDoczytywanie(true);
    const kontener = przewijaneRef.current;
    const wysokoscPrzed = kontener ? kontener.scrollHeight : 0;
    const pozycjaPrzed = kontener ? kontener.scrollTop : 0;
    try {
      const nowyOffset = Math.max(0, zakresOd - STRONA_WIADOMOSCI);
      const limit = zakresOd - nowyOffset;
      const res = await api.get(`/komunikator/admin/rozmowy/${rozmowa.id}/wiadomosci`, { params: { limit, offset: nowyOffset } });
      const d = trescOdpowiedzi(res);
      const lista = d && Array.isArray(d.wiadomosci) ? d.wiadomosci : [];
      setWiadomosci((poprzednie) => [...lista, ...poprzednie]);
      setZakresOd(nowyOffset);
      window.requestAnimationFrame(() => {
        if (przewijaneRef.current) {
          przewijaneRef.current.scrollTop = pozycjaPrzed + (przewijaneRef.current.scrollHeight - wysokoscPrzed);
        }
      });
    } catch (e) {
      toast.error(tekstBledu(e, 'Nie udało się doczytać starszych wiadomości'));
    } finally {
      setDoczytywanie(false);
    }
  }, [rozmowa, zakresOd, doczytywanie]);

  const doczytajNowsze = useCallback(async () => {
    if (!rozmowa || zakresDo >= wiadomosciTotal || doczytywanie) return;
    setDoczytywanie(true);
    try {
      const res = await api.get(`/komunikator/admin/rozmowy/${rozmowa.id}/wiadomosci`, { params: { limit: STRONA_WIADOMOSCI, offset: zakresDo } });
      const d = trescOdpowiedzi(res);
      const lista = d && Array.isArray(d.wiadomosci) ? d.wiadomosci : [];
      setWiadomosci((poprzednie) => [...poprzednie, ...lista]);
      setZakresDo(zakresDo + lista.length);
    } catch (e) {
      toast.error(tekstBledu(e, 'Nie udało się doczytać nowszych wiadomości'));
    } finally {
      setDoczytywanie(false);
    }
  }, [rozmowa, zakresDo, wiadomosciTotal, doczytywanie]);

  // Przewijanie: na dol przy otwarciu, albo do wskazanej wiadomosci z wyszukiwarki.
  useEffect(() => {
    if (historiaLoading) return;
    const cel = podswietlona ? wiadomosciRef.current[podswietlona] : null;
    if (cel) {
      if (typeof cel.scrollIntoView === 'function') cel.scrollIntoView({ block: 'center' });
      przewinDoDolu.current = false;
      return;
    }
    if (przewinDoDolu.current && przewijaneRef.current) {
      przewijaneRef.current.scrollTop = przewijaneRef.current.scrollHeight;
      przewinDoDolu.current = false;
    }
  }, [wiadomosci, historiaLoading, podswietlona]);

  // -------------------------------------------------------------------------
  // Wyszukiwarka
  // -------------------------------------------------------------------------

  const szukaj = useCallback(async () => {
    const q = fraza.trim();
    if (q.length < 2) {
      toast.error('Podaj co najmniej 2 znaki do wyszukania');
      return;
    }
    setSzukanieLoading(true);
    try {
      const params = { q };
      if (uzyteFiltry.typ) params.typ = uzyteFiltry.typ;
      if (uzyteFiltry.login) params.login = uzyteFiltry.login;
      if (uzyteFiltry.od) params.od = uzyteFiltry.od;
      if (uzyteFiltry.do) params.do = uzyteFiltry.do;

      const res = await api.get('/komunikator/admin/szukaj', { params });
      const d = trescOdpowiedzi(res);
      if (!d) throw new Error('Nieprawidłowa odpowiedź serwera');
      setWyniki(Array.isArray(d.wyniki) ? d.wyniki : []);
      setWynikiObciete(Boolean(d.obciete));
    } catch (e) {
      setWyniki([]);
      setWynikiObciete(false);
      toast.error(tekstBledu(e, 'Błąd wyszukiwania'));
    } finally {
      setSzukanieLoading(false);
    }
  }, [fraza, uzyteFiltry]);

  const wyczyscSzukanie = () => {
    setFraza('');
    setWyniki(null);
    setWynikiObciete(false);
  };

  const zastosujFiltry = () => {
    setUzyteFiltry(filtry);
    setWyniki(null);
    wczytajRozmowy(filtry, false);
  };

  const wyczyscFiltry = () => {
    const puste = { typ: '', login: '', od: '', do: '' };
    setFiltry(puste);
    setUzyteFiltry(puste);
    setWyniki(null);
    wczytajRozmowy(puste, false);
  };

  const ustawTyp = (typ) => {
    const nowe = { ...filtry, typ };
    setFiltry(nowe);
    setUzyteFiltry(nowe);
    setWyniki(null);
    wczytajRozmowy(nowe, false);
  };

  // -------------------------------------------------------------------------
  // Eksport calej rozmowy (bez nowego endpointu - petla po istniejacej paginacji)
  // -------------------------------------------------------------------------

  const pobierzCalaHistorie = async (rozmowaId) => {
    const zebrane = [];
    let offset = 0;
    let total = 0;
    do {
      // eslint-disable-next-line no-await-in-loop
      const res = await api.get(`/komunikator/admin/rozmowy/${rozmowaId}/wiadomosci`, { params: { limit: STRONA_EKSPORTU, offset } });
      const d = trescOdpowiedzi(res);
      if (!d) throw new Error('Nieprawidłowa odpowiedź serwera');
      total = Number(d.total) || 0;
      const lista = Array.isArray(d.wiadomosci) ? d.wiadomosci : [];
      if (lista.length === 0) break;
      zebrane.push(...lista);
      offset += lista.length;
    } while (offset < total && zebrane.length < MAX_WIADOMOSCI_EKSPORT);
    return zebrane;
  };

  const zapiszPlik = (tresc, nazwa, mime) => {
    const BOM = '﻿';
    const blob = new Blob([BOM + tresc], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = nazwa;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };

  // Tresci czatu zawieraja srednik, cudzyslow i znaki nowej linii - kazde pole
  // musi byc opakowane, a cudzyslow podwojony. Inaczej Excel rozjedzie kolumny.
  const komorkaCsv = (wartosc) => `"${String(wartosc === null || wartosc === undefined ? '' : wartosc).replace(/"/g, '""')}"`;

  const eksportuj = async (format) => {
    if (!rozmowa || eksportTrwa) return;
    setEksportTrwa(true);
    const notka = toast.loading('Pobieranie pełnej historii…');
    try {
      const lista = await pobierzCalaHistorie(rozmowa.id);
      if (lista.length === 0) {
        toast.error('Rozmowa nie zawiera wiadomości', { id: notka });
        return;
      }

      const opisZalacznikow = (w) => (w.zalaczniki || []).map((z) => z.plik_nazwa || `plik ${z.id}`).join(' | ');
      const bezpiecznaNazwa = String(rozmowa.nazwa || `rozmowa_${rozmowa.id}`)
        .replace(/[^\p{L}\p{N}]+/gu, '_')
        .replace(/^_+|_+$/g, '')
        .slice(0, 60) || `rozmowa_${rozmowa.id}`;
      const stempel = dzisiajKlucz();

      if (format === 'csv') {
        const wiersze = [
          ['Data', 'Autor', 'Typ wiadomosci', 'Usunieta', 'Data usuniecia', 'Tresc', 'Zalaczniki'],
          ...lista.map((w) => [
            w.utworzono || '',
            w.autor_nazwa || w.autor_login || '',
            w.typ || '',
            w.usunieta ? 'TAK' : 'NIE',
            w.usunieta_at || '',
            w.tresc || '',
            opisZalacznikow(w)
          ])
        ];
        const csv = wiersze.map((r) => r.map(komorkaCsv).join(';')).join('\r\n');
        zapiszPlik(csv, `komunikator_${bezpiecznaNazwa}_${stempel}.csv`, 'text/csv;charset=utf-8;');
      } else {
        const linie = [
          `Komunikator V3SYS - pełna historia rozmowy`,
          `Rozmowa: ${rozmowa.nazwa || rozmowa.id} (ID ${rozmowa.id}, typ: ${rozmowa.typ})`,
          rozmowa.uczestnicy ? `Uczestnicy: ${rozmowa.uczestnicy}` : `Uczestnicy: kanał otwarty`,
          `Wiadomości: ${lista.length}`,
          `Wyeksportowano: ${new Date().toLocaleString('pl-PL')}`,
          '='.repeat(72),
          ''
        ];
        lista.forEach((w) => {
          linie.push(`[${w.utworzono || '?'}] ${w.autor_nazwa || w.autor_login || '?'}:`);
          if (w.tresc) linie.push(w.tresc);
          const zal = opisZalacznikow(w);
          if (zal) linie.push(`  [załączniki] ${zal}`);
          if (w.usunieta) linie.push(`  [USUNIĘTA PRZEZ AUTORA${w.usunieta_at ? ` ${w.usunieta_at}` : ''} - treść zachowana w archiwum]`);
          linie.push('');
        });
        zapiszPlik(linie.join('\r\n'), `komunikator_${bezpiecznaNazwa}_${stempel}.txt`, 'text/plain;charset=utf-8;');
      }

      const ostrzezenie = lista.length >= MAX_WIADOMOSCI_EKSPORT ? ' (obcięto do limitu)' : '';
      toast.success(`Wyeksportowano ${lista.length} wiadomości${ostrzezenie}`, { id: notka });
    } catch (e) {
      toast.error(tekstBledu(e, 'Błąd eksportu rozmowy'), { id: notka });
    } finally {
      setEksportTrwa(false);
    }
  };

  // -------------------------------------------------------------------------
  // Render pomocniczy
  // -------------------------------------------------------------------------

  const renderWiersz = (r) => {
    const wybrana = rozmowa && rozmowa.id === r.id;
    return (
      <button
        key={r.id}
        type="button"
        onClick={() => otworzRozmowe(r.id, null)}
        className={`kom-adm-wiersz${wybrana ? ' kom-adm-wybrany' : ''}`}
        style={{
          width: '100%',
          textAlign: 'left',
          padding: '0.5rem 0.6rem',
          borderRadius: '6px',
          cursor: 'pointer',
          fontSize: '12px',
          fontWeight: 500,
          backgroundColor: wybrana ? 'var(--primary)' : 'var(--card-bg)',
          border: `1px solid ${wybrana ? 'var(--primary)' : 'var(--border)'}`,
          borderLeft: `3px solid ${wybrana ? 'var(--primary)' : (KOLOR_TYPU[r.typ] || 'var(--border)')}`,
          color: wybrana ? '#fff' : 'var(--text)',
          display: 'block',
          minWidth: 0
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', minWidth: 0 }}>
          <span aria-hidden="true">{IKONA_TYPU[r.typ] || '💬'}</span>
          <span style={{
            fontWeight: 600,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flex: 1,
            minWidth: 0
          }}>
            {r.nazwa || `Rozmowa ${r.id}`}
          </span>
        </div>
        <div style={{
          fontSize: '11px',
          marginTop: '0.2rem',
          color: wybrana ? 'rgba(255,255,255,0.85)' : 'var(--text-muted)',
          display: 'flex',
          gap: '0.5rem',
          flexWrap: 'wrap'
        }}>
          <span>{r.liczba_wiadomosci} wiad.</span>
          <span>{r.liczba_uczestnikow} uczestn.</span>
          <span>{fmtDataGodzina(r.ostatnia_aktywnosc)}</span>
        </div>
      </button>
    );
  };

  const renderWynik = (w) => (
    <button
      key={w.id}
      type="button"
      onClick={() => otworzRozmowe(w.rozmowa_id, w.id)}
      className="kom-adm-wiersz"
      style={{
        width: '100%',
        textAlign: 'left',
        padding: '0.5rem 0.6rem',
        borderRadius: '6px',
        cursor: 'pointer',
        fontSize: '12px',
        fontWeight: 500,
        backgroundColor: podswietlona === w.id ? 'var(--primary-light)' : 'var(--card-bg)',
        border: '1px solid var(--border)',
        borderLeft: `3px solid ${KOLOR_TYPU[w.rozmowa_typ] || 'var(--border)'}`,
        color: 'var(--text)',
        display: 'block',
        minWidth: 0
      }}
    >
      <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '0.2rem' }}>
        <span style={plakietka(KOLOR_TYPU[w.rozmowa_typ] || 'var(--text-muted)')}>
          {skrot(w.rozmowa_nazwa || `Rozmowa ${w.rozmowa_id}`, 40)}
        </span>
        {w.usunieta && <span style={plakietka('var(--danger)')}>usunięta</span>}
      </div>
      <div style={{
        color: 'var(--text)',
        overflowWrap: 'anywhere',
        whiteSpace: 'pre-wrap',
        maxHeight: '3.4rem',
        overflow: 'hidden'
      }}>
        {skrot(w.tresc, 180)}
      </div>
      <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '0.2rem' }}>
        {w.autor_nazwa || w.autor_login} · {fmtDataGodzina(w.utworzono)}
      </div>
    </button>
  );

  const renderWiadomosc = (w, poprzednia) => {
    const dzien = dzienZTekstu(w.utworzono);
    const poprzedniDzien = poprzednia ? dzienZTekstu(poprzednia.utworzono) : null;
    const nowyDzien = dzien && dzien !== poprzedniDzien;
    const podswietl = podswietlona === w.id;

    return (
      <React.Fragment key={w.id}>
        {nowyDzien && (
          <div style={{
            textAlign: 'center',
            fontSize: '11px',
            fontWeight: 600,
            color: 'var(--text-muted)',
            margin: '0.75rem 0 0.35rem',
            position: 'relative'
          }}>
            <span style={{
              backgroundColor: 'var(--bg)',
              border: '1px solid var(--border)',
              borderRadius: '999px',
              padding: '2px 10px'
            }}>
              {fmtDzienOpisowo(dzien)}
            </span>
          </div>
        )}
        <div
          ref={(el) => { if (el) wiadomosciRef.current[w.id] = el; }}
          style={{
            padding: '0.5rem 0.65rem',
            marginBottom: '0.4rem',
            borderRadius: '6px',
            border: `1px solid ${podswietl ? 'var(--primary)' : 'var(--border)'}`,
            borderLeft: `3px solid ${w.usunieta ? 'var(--danger)' : (podswietl ? 'var(--primary)' : 'var(--border)')}`,
            backgroundColor: w.usunieta ? 'var(--danger-light)' : (podswietl ? 'var(--primary-light)' : 'var(--card-bg)'),
            minWidth: 0
          }}
        >
          <div style={{
            display: 'flex',
            gap: '0.5rem',
            alignItems: 'baseline',
            flexWrap: 'wrap',
            marginBottom: '0.25rem'
          }}>
            <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text)' }}>
              {w.autor_nazwa || w.autor_login || '-'}
            </span>
            <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
              {fmtGodzina(w.utworzono)}
            </span>
            {w.typ === 'system' && <span style={plakietka('var(--text-muted)')}>systemowa</span>}
            {w.usunieta && (
              <span style={plakietka('var(--danger)')}>
                usunięta przez autora{w.usunieta_at ? ` · ${fmtDataGodzina(w.usunieta_at)}` : ''}
              </span>
            )}
          </div>

          {w.tresc ? (
            <div style={{
              fontSize: '12px',
              lineHeight: 1.5,
              color: w.usunieta ? 'var(--text-secondary)' : 'var(--text)',
              whiteSpace: 'pre-wrap',
              overflowWrap: 'anywhere',
              textDecoration: w.usunieta ? 'line-through' : 'none'
            }}>
              {w.tresc}
            </div>
          ) : (
            <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontStyle: 'italic' }}>
              (brak treści)
            </div>
          )}

          {w.usunieta && (
            <div style={{ fontSize: '10px', color: 'var(--danger)', marginTop: '0.25rem' }}>
              Pracownicy widzą tu „Wiadomość usunięta” — powyżej oryginalna treść z archiwum.
            </div>
          )}

          {Array.isArray(w.zalaczniki) && w.zalaczniki.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginTop: '0.45rem' }}>
              {w.zalaczniki.map((z) => <Zalacznik key={z.id} zalacznik={z} />)}
            </div>
          )}
        </div>
      </React.Fragment>
    );
  };

  // -------------------------------------------------------------------------
  // Widok
  // -------------------------------------------------------------------------

  const wTrybieSzukania = wyniki !== null;

  return (
    <div style={{ minWidth: 0 }}>
      <style>{`
        .kom-adm-uklad {
          display: grid;
          grid-template-columns: 360px minmax(0, 1fr);
          gap: 0.75rem;
          align-items: start;
        }
        .kom-adm-staty {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 0.5rem;
          margin-bottom: 0.75rem;
        }
        .kom-adm-filtry {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 0.5rem;
        }
        /* Podswietlenie kursorem nie moze zjesc zaznaczenia: regula motywu jest
           z !important, wiec bije tlo podane inline - stad wykluczenie. */
        .kom-adm-wiersz:not(.kom-adm-wybrany):hover { background-color: var(--card-bg-hover) !important; }
        .kom-adm-panel { height: calc(100vh - 330px); min-height: 380px; }
        @media (max-width: 1100px) {
          .kom-adm-staty { grid-template-columns: repeat(2, minmax(0, 1fr)); }
        }
        @media (max-width: 900px) {
          .kom-adm-uklad { grid-template-columns: minmax(0, 1fr); }
          .kom-adm-panel { height: auto; max-height: 70vh; min-height: 300px; }
        }
        @media (max-width: 600px) {
          .kom-adm-filtry { grid-template-columns: minmax(0, 1fr); }
        }
      `}</style>

      {/* ---------------- Statystyki ---------------- */}
      <div className="kom-adm-staty">
        <Kafelek
          ikona="💬"
          etykieta="Wiadomości dzisiaj"
          wartosc={statyLoading ? '…' : `${staty && staty.niepelne ? '≥ ' : ''}${staty ? staty.wiadomosciDzis : '-'}`}
          kolor="var(--primary)"
        />
        <Kafelek
          ikona="🗂️"
          etykieta="Rozmowy aktywne dzisiaj"
          wartosc={statyLoading ? '…' : (staty ? staty.rozmowyDzis : '-')}
          kolor="var(--info)"
        />
        <Kafelek
          ikona="👥"
          etykieta="Pracownicy piszący dzisiaj"
          wartosc={statyLoading ? '…' : (staty ? staty.piszacyDzis : '-')}
          kolor="var(--purple)"
        />
        <Kafelek
          ikona="📚"
          etykieta="Rozmowy w systemie"
          wartosc={statyLoading ? '…' : (staty ? staty.rozmowyWSystemie : '-')}
          kolor="var(--text)"
        />
      </div>

      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        flexWrap: 'wrap',
        marginBottom: '0.75rem'
      }}>
        <button type="button" style={malyPrzycisk(false)} onClick={przeliczStatystyki} disabled={statyLoading}>
          🔄 {statyLoading ? 'Liczenie…' : 'Przelicz statystyki'}
        </button>
        {staty && staty.niepelne && (
          <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
            Znak „≥” oznacza, że dzisiejszy ruch przekroczył okno podglądu — faktyczna liczba może być wyższa.
          </span>
        )}
      </div>

      {/* ---------------- Filtry + wyszukiwarka ---------------- */}
      <div style={{ ...kartaStyl, padding: '0.75rem', marginBottom: '0.75rem' }}>
        <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap', marginBottom: '0.6rem' }}>
          {TYPY.map((t) => (
            <button
              key={t.id || 'wszystkie'}
              type="button"
              style={przycisk(filtry.typ === t.id, KOLOR_TYPU[t.id] || 'var(--primary)')}
              onClick={() => ustawTyp(t.id)}
            >
              <span aria-hidden="true">{t.ikona}</span> {t.label}
            </button>
          ))}
        </div>

        <div className="kom-adm-filtry">
          <div>
            <label style={etykietaStyl} htmlFor="kom-adm-login">Pracownik</label>
            <input
              id="kom-adm-login"
              list="kom-adm-pracownicy"
              type="text"
              value={filtry.login}
              onChange={(e) => setFiltry({ ...filtry, login: e.target.value })}
              onKeyDown={(e) => { if (e.key === 'Enter') zastosujFiltry(); }}
              placeholder="login (dowolny uczestnik)"
              style={polePolStyl}
            />
            <datalist id="kom-adm-pracownicy">
              {pracownicy.map((p) => <option key={p} value={p} />)}
            </datalist>
          </div>
          <div>
            <label style={etykietaStyl} htmlFor="kom-adm-od">Od dnia</label>
            <input
              id="kom-adm-od"
              type="date"
              value={filtry.od}
              onChange={(e) => setFiltry({ ...filtry, od: e.target.value })}
              style={polePolStyl}
            />
          </div>
          <div>
            <label style={etykietaStyl} htmlFor="kom-adm-do">Do dnia</label>
            <input
              id="kom-adm-do"
              type="date"
              value={filtry.do}
              onChange={(e) => setFiltry({ ...filtry, do: e.target.value })}
              style={polePolStyl}
            />
          </div>
        </div>

        <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginTop: '0.6rem' }}>
          <button type="button" style={przycisk(true)} onClick={zastosujFiltry} disabled={rozmowyLoading}>
            🔎 Filtruj rozmowy
          </button>
          <button type="button" style={przycisk(false)} onClick={wyczyscFiltry}>
            ✖ Wyczyść filtry
          </button>
        </div>

        <div style={{
          display: 'flex',
          gap: '0.4rem',
          flexWrap: 'wrap',
          alignItems: 'flex-end',
          marginTop: '0.75rem',
          paddingTop: '0.75rem',
          borderTop: '1px solid var(--border)'
        }}>
          <div style={{ flex: '1 1 260px', minWidth: 0 }}>
            <label style={etykietaStyl} htmlFor="kom-adm-fraza">
              Szukaj w treści całej historii (także w wiadomościach usuniętych)
            </label>
            <input
              id="kom-adm-fraza"
              type="text"
              value={fraza}
              onChange={(e) => setFraza(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') szukaj(); }}
              placeholder="min. 2 znaki, np. faktura, zwrot, kasa"
              style={polePolStyl}
            />
          </div>
          <button type="button" style={przycisk(true, 'var(--info)')} onClick={szukaj} disabled={szukanieLoading}>
            🔍 {szukanieLoading ? 'Szukam…' : 'Szukaj'}
          </button>
          {wTrybieSzukania && (
            <button type="button" style={przycisk(false)} onClick={wyczyscSzukanie}>
              ↩ Wróć do listy rozmów
            </button>
          )}
        </div>
        <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '0.4rem' }}>
          Filtry typu, pracownika i dat obowiązują także przy wyszukiwaniu (pracownik = autor wiadomości).
        </div>
      </div>

      {/* ---------------- Dwie kolumny ---------------- */}
      <div className="kom-adm-uklad">

        {/* LEWA: lista rozmow / wyniki wyszukiwania */}
        <div style={{ ...kartaStyl, display: 'flex', flexDirection: 'column', minWidth: 0 }} className="kom-adm-panel">
          <div style={naglowekKartyStyl}>
            <span>
              {wTrybieSzukania
                ? `🔍 Wyniki wyszukiwania (${wyniki.length})`
                : `🗂️ Rozmowy (${rozmowy.length} z ${rozmowyTotal})`}
            </span>
            {!wTrybieSzukania && (
              <button
                type="button"
                style={malyPrzycisk(false)}
                onClick={() => wczytajRozmowy(uzyteFiltry, false)}
                disabled={rozmowyLoading}
              >
                🔄 Odśwież
              </button>
            )}
          </div>

          {wynikiObciete && wTrybieSzukania && (
            <div style={{
              padding: '0.4rem 0.6rem',
              fontSize: '11px',
              color: 'var(--warning)',
              borderBottom: '1px solid var(--border)'
            }}>
              Lista obcięta do limitu — doprecyzuj frazę lub zawęź daty.
            </div>
          )}

          <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.35rem', minWidth: 0 }}>
            {rozmowyBlad && !wTrybieSzukania && (
              <div style={{ padding: '0.6rem', fontSize: '12px', color: 'var(--danger)', backgroundColor: 'var(--danger-light)', borderRadius: '6px' }}>
                {rozmowyBlad}
              </div>
            )}

            {wTrybieSzukania ? (
              wyniki.length === 0
                ? <div style={{ padding: '1.5rem 0.5rem', textAlign: 'center', fontSize: '12px', color: 'var(--text-muted)' }}>
                    {szukanieLoading ? 'Szukam…' : 'Brak wiadomości pasujących do zapytania'}
                  </div>
                : wyniki.map(renderWynik)
            ) : (
              rozmowy.length === 0
                ? <div style={{ padding: '1.5rem 0.5rem', textAlign: 'center', fontSize: '12px', color: 'var(--text-muted)' }}>
                    {rozmowyLoading ? 'Wczytywanie…' : 'Brak rozmów spełniających kryteria'}
                  </div>
                : rozmowy.map(renderWiersz)
            )}

            {!wTrybieSzukania && rozmowy.length > 0 && rozmowy.length < rozmowyTotal && (
              <button
                type="button"
                style={{ ...malyPrzycisk(false), justifyContent: 'center', width: '100%' }}
                onClick={() => wczytajRozmowy(uzyteFiltry, true)}
                disabled={rozmowyLoading}
              >
                ⬇ Wczytaj kolejne ({rozmowyTotal - rozmowy.length})
              </button>
            )}
          </div>
        </div>

        {/* PRAWA: historia rozmowy */}
        <div style={{ ...kartaStyl, display: 'flex', flexDirection: 'column', minWidth: 0 }} className="kom-adm-panel">
          <div style={naglowekKartyStyl}>
            <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {rozmowa
                ? `${IKONA_TYPU[rozmowa.typ] || '💬'} ${rozmowa.nazwa || `Rozmowa ${rozmowa.id}`}`
                : '💬 Historia rozmowy'}
            </span>
            {rozmowa && (
              <span style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
                <button type="button" style={malyPrzycisk(false)} onClick={() => otworzRozmowe(rozmowa.id, null)} disabled={historiaLoading}>
                  🔄 Odśwież
                </button>
                <button type="button" style={malyPrzycisk(false)} onClick={() => eksportuj('csv')} disabled={eksportTrwa}>
                  ⬇ CSV
                </button>
                <button type="button" style={malyPrzycisk(false)} onClick={() => eksportuj('txt')} disabled={eksportTrwa}>
                  ⬇ TXT
                </button>
              </span>
            )}
          </div>

          {rozmowa && (
            <div style={{
              padding: '0.4rem 0.6rem',
              borderBottom: '1px solid var(--border)',
              fontSize: '11px',
              color: 'var(--text-muted)',
              display: 'flex',
              gap: '0.5rem',
              flexWrap: 'wrap',
              alignItems: 'center'
            }}>
              <span style={plakietka(KOLOR_TYPU[rozmowa.typ] || 'var(--text-muted)')}>
                {rozmowa.typ === 'globalny' ? 'cała sieć' : rozmowa.typ}
              </span>
              {rozmowa.lokalizacja_nazwa && <span>Lokalizacja: {rozmowa.lokalizacja_nazwa}</span>}
              {rozmowa.uczestnicy && (
                <span style={{ overflowWrap: 'anywhere' }}>Uczestnicy: {rozmowa.uczestnicy}</span>
              )}
              <span>
                Wiadomości: {wiadomosciTotal}
                {wiadomosciTotal > 0 && ` · pokazano ${zakresOd + 1}-${zakresDo}`}
              </span>
              <span>Utworzono: {fmtDataGodzina(rozmowa.utworzono)}</span>
            </div>
          )}

          <div
            ref={przewijaneRef}
            style={{ flex: 1, overflowY: 'auto', overflowX: 'auto', padding: '0.6rem', minWidth: 0 }}
          >
            {historiaBlad && (
              <div style={{ padding: '0.6rem', fontSize: '12px', color: 'var(--danger)', backgroundColor: 'var(--danger-light)', borderRadius: '6px' }}>
                {historiaBlad}
              </div>
            )}

            {!rozmowa && !historiaLoading && !historiaBlad && (
              <div style={{ padding: '2.5rem 1rem', textAlign: 'center', fontSize: '12px', color: 'var(--text-muted)' }}>
                Wybierz rozmowę z listy po lewej albo znajdź konkretną wiadomość wyszukiwarką.
              </div>
            )}

            {historiaLoading && (
              <div style={{ padding: '2.5rem 1rem', textAlign: 'center', fontSize: '12px', color: 'var(--text-muted)' }}>
                Wczytywanie historii…
              </div>
            )}

            {!historiaLoading && rozmowa && zakresOd > 0 && (
              <button
                type="button"
                style={{ ...malyPrzycisk(false), width: '100%', justifyContent: 'center', marginBottom: '0.5rem' }}
                onClick={doczytajStarsze}
                disabled={doczytywanie}
              >
                ⬆ {doczytywanie ? 'Wczytywanie…' : `Wczytaj starsze (pozostało ${zakresOd})`}
              </button>
            )}

            {!historiaLoading && rozmowa && wiadomosci.length === 0 && !historiaBlad && (
              <div style={{ padding: '2rem 1rem', textAlign: 'center', fontSize: '12px', color: 'var(--text-muted)' }}>
                Ta rozmowa nie zawiera jeszcze żadnych wiadomości.
              </div>
            )}

            {!historiaLoading && wiadomosci.map((w, i) => renderWiadomosc(w, i > 0 ? wiadomosci[i - 1] : null))}

            {!historiaLoading && rozmowa && zakresDo < wiadomosciTotal && (
              <button
                type="button"
                style={{ ...malyPrzycisk(false), width: '100%', justifyContent: 'center', marginTop: '0.5rem' }}
                onClick={doczytajNowsze}
                disabled={doczytywanie}
              >
                ⬇ {doczytywanie ? 'Wczytywanie…' : `Wczytaj nowsze (pozostało ${wiadomosciTotal - zakresDo})`}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default KomunikatorTab;
